import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Anchor, 
  ArrowLeft,
  Calendar, 
  MapPin, 
  Clock, 
  Ship, 
  User,
  Users,
  Phone,
  Mail,
  Navigation,
  Loader2,
  Car,
  CreditCard,
  CheckCircle2,
  Download,
  Printer,
  Receipt
} from "lucide-react";
import axios from "axios";
import { API_URL } from "@/config/api";

// Status badge colors
const statusColors = {
  pending: "bg-amber-100 text-amber-800",
  assigned: "bg-blue-100 text-blue-800",
  in_progress: "bg-purple-100 text-purple-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800"
};

const paymentStatusLabels = {
  pending: "Payment Pending",
  paid: "Paid Online",
  paid_by_cash: "Paid by Cash"
};

export default function ClientBookingDetailPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("client_token");
    if (!token) {
      navigate("/client/login", { state: { from: `/client/booking/${bookingId}` } });
      return;
    }
    
    fetchBooking(token);
  }, [bookingId, navigate]);

  const fetchBooking = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/client/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBooking(response.data);
    } catch (err) {
      console.error("Failed to fetch booking:", err);
      if (err.response?.status === 401) {
        navigate("/client/login");
      } else if (err.response?.status === 404) {
        setError("Booking not found");
      } else {
        setError("Failed to load booking details");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "";
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-slate-600 mb-4">{error}</p>
            <Link to="/client/dashboard">
              <Button>Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50 print:hidden">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/client/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight">Y GO</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Receipt Card */}
        <Card className="shadow-lg" ref={receiptRef}>
          {/* Receipt Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Anchor className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="font-bold text-lg">Y GO</h1>
                  <p className="text-orange-100 text-xs">Seamen Shore Services</p>
                </div>
              </div>
              <Badge className={`${statusColors[booking.status]} text-sm`}>
                {booking.status}
              </Badge>
            </div>
            <div className="text-center">
              <p className="text-orange-100 text-sm mb-1">Booking Reference</p>
              <p className="font-mono text-2xl font-bold">{booking.booking_id}</p>
            </div>
          </div>

          <CardContent className="p-6">
            {/* Booking Details */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Ship className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Ship</p>
                  <p className="font-medium">{booking.ship_name}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Terminal</p>
                  <p className="font-medium">{booking.terminal_name}</p>
                  {booking.pickup_address && (
                    <p className="text-sm text-slate-600">{booking.pickup_address}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Pickup Date & Time</p>
                  <p className="font-medium">{formatDate(booking.pickup_datetime)}</p>
                  <p className="text-slate-600">{formatTime(booking.pickup_datetime)}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Passengers</p>
                  <p className="font-medium">{booking.passengers} passenger{booking.passengers > 1 ? 's' : ''}</p>
                </div>
              </div>

              <Separator />

              {/* Passenger Info */}
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Passenger Name</p>
                  <p className="font-medium">{booking.passenger_name}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{booking.phone_number}</p>
                </div>
              </div>
              
              {booking.client_email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{booking.client_email}</p>
                  </div>
                </div>
              )}

              {/* Driver Info */}
              {booking.assigned_driver_name && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <Car className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-500">Driver</p>
                      <p className="font-medium">{booking.assigned_driver_name}</p>
                      {(booking.driver_vehicle_brand || booking.driver_vehicle_model) && (
                        <p className="text-sm text-slate-600">
                          {[booking.driver_vehicle_brand, booking.driver_vehicle_model].filter(Boolean).join(' ')}
                        </p>
                      )}
                      {booking.driver_license_plate && (
                        <Badge variant="outline" className="mt-1 font-mono">
                          {booking.driver_license_plate}
                        </Badge>
                      )}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Payment Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-600">Base Price</span>
                  <span>€{booking.base_price?.toFixed(2)}</span>
                </div>
                {booking.night_surcharge_applied && (
                  <div className="flex items-center justify-between mb-3 text-sm">
                    <span className="text-slate-500">Night Surcharge (20%)</span>
                    <span>+€{((booking.final_price || 0) - (booking.base_price || 0)).toFixed(2)}</span>
                  </div>
                )}
                <Separator className="my-3" />
                <div className="flex items-center justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-orange-600">€{booking.final_price?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-3">
                  {booking.payment_status === "paid" || booking.payment_status === "paid_by_cash" ? (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {paymentStatusLabels[booking.payment_status]}
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800">
                      <CreditCard className="w-3 h-3 mr-1" />
                      {paymentStatusLabels[booking.payment_status]}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Created Date */}
              <div className="text-center text-sm text-slate-500 mt-4">
                <p>Booked on {formatDate(booking.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="mt-6 flex gap-3 print:hidden">
          {(booking.status === "assigned" || booking.status === "in_progress") && (
            <Link to={`/track/${booking.tracking_token || booking.booking_id}`} className="flex-1">
              <Button className="w-full bg-orange-500 hover:bg-orange-600">
                <Navigation className="w-4 h-4 mr-2" />
                Track Driver
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm print:hidden">
          <p>Y GO - Seamen Shore Services</p>
          <p>Le Havre, France | +33 2 78 78 04 04</p>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
